import { useEffect } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuthStore } from './state/authStore.ts';
import { authApi } from './api/auth.ts';
import { accountApi } from './api/account.ts';
import { getStoredGclid, trackEvent } from './analytics.ts';
import NavBar from './components/NavBar.tsx';
import ConsentBanner from './components/ConsentBanner.tsx';
import StickyCta from './components/StickyCta.tsx';
import LandingPage from './pages/LandingPage.tsx';
import LandingComplianceLP from './pages/LandingComplianceLP.tsx';
import LandingScannerLP from './pages/LandingScannerLP.tsx';
import SignInPage from './pages/SignInPage.tsx';
import ScanResultPage from './pages/ScanResultPage.tsx';
import PricingPage from './pages/PricingPage.tsx';
import SamplePage from './pages/SamplePage.tsx';
import BillingSuccessPage from './pages/BillingSuccessPage.tsx';
import ProfilePage from './pages/ProfilePage.tsx';
import VerifyEmailPage from './pages/VerifyEmailPage.tsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.tsx';
import ResetPasswordPage from './pages/ResetPasswordPage.tsx';
import PrivacyPage from './pages/PrivacyPage.tsx';
import TermsPage from './pages/TermsPage.tsx';
// import ImprintPage from './pages/ImprintPage.tsx'; // disabled until real legal data is filled in
import RefundPolicyPage from './pages/RefundPolicyPage.tsx';
import FaqPage from './pages/FaqPage.tsx';
import CompetitorComparisonPage from './pages/CompetitorComparisonPage.tsx';
import { ACCESSIBE, USERWAY, SITEIMPROVE } from './pages/competitors.tsx';
import AboutPage from './pages/AboutPage.tsx';
import ChangelogPage from './pages/ChangelogPage.tsx';
import StatusPage from './pages/StatusPage.tsx';
import DpaPage from './pages/DpaPage.tsx';
import RoiCalculatorPage from './pages/RoiCalculatorPage.tsx';
import LandingComplianceLpDe from './pages/LandingComplianceLpDe.tsx';
import LandingComplianceLpFr from './pages/LandingComplianceLpFr.tsx';
import NotFoundPage from './pages/NotFoundPage.tsx';
import AccessibilityPage from './pages/AccessibilityPage.tsx';
import VerifyPage from './pages/VerifyPage.tsx';

export default function App() {
  const setUser = useAuthStore(s => s.setUser);
  const { pathname } = useLocation();

  // Bootstrap: ask the API who we are (may be null) so the NavBar can render correctly
  // and so we have a CSRF cookie before the first mutation.
  useEffect(() => {
    (async () => {
      try {
        const me = await authApi.me();
        setUser(me);

        if (me) {
          // Once we know who the caller is, ship any captured Google Ads click id to the API
          // so it lands on the User entity and gets included in server-side conversion uploads
          // on the eventual purchase + every renewal. First-touch + idempotent on the server.
          const gclid = getStoredGclid();
          if (gclid) {
            void accountApi.recordGclid(gclid);
          }

          // Detect a brand-new Google OAuth signup: the server appends ?new_user=1 on the
          // post-OAuth redirect so we can fire sign_up exactly once without a separate API
          // call. Consume the param immediately so hard-refreshes don't double-fire.
          const params = new URLSearchParams(window.location.search);
          if (params.get('new_user') === '1') {
            trackEvent('sign_up', { method: 'google' });
            params.delete('new_user');
            const newSearch = params.toString();
            const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
            window.history.replaceState(null, '', newUrl);
          }
        }
      } catch {
        setUser(null);
      }
    })();
  }, [setUser]);

  return (
    <div className="app">
      {/* WCAG 2.4.1 Bypass Blocks — first focusable element jumps past the nav. */}
      <a href="#main" className="skip-link">Skip to main content</a>
      <NavBar />
      <main id="main" tabIndex={-1} className={pathname === '/' ? 'main-landing' : undefined}>
        <Sentry.ErrorBoundary fallback={<RouteErrorFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            {/* Paid-ads landing pages. noindex'd so they don't compete with the home page in
                organic. Match the ad headline word-for-word to the LP H1 — that's the QS lift. */}
            <Route path="/lp/eaa-compliance" element={<LandingComplianceLP />} />
            <Route path="/lp/wcag-scanner" element={<LandingScannerLP />} />
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            {/* <Route path="/imprint" element={<ImprintPage />} /> — disabled until legal data is filled in */}
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/dpa" element={<DpaPage />} />
            <Route path="/roi-calculator" element={<RoiCalculatorPage />} />
            <Route path="/de/lp/eaa-compliance" element={<LandingComplianceLpDe />} />
            <Route path="/fr/lp/eaa-compliance" element={<LandingComplianceLpFr />} />
            <Route path="/vs/accessibe" element={<CompetitorComparisonPage profile={ACCESSIBE} />} />
            <Route path="/vs/userway" element={<CompetitorComparisonPage profile={USERWAY} />} />
            <Route path="/vs/siteimprove" element={<CompetitorComparisonPage profile={SITEIMPROVE} />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/scan/:id" element={<ScanResultPage />} />
            <Route path="/verify/:token" element={<VerifyPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/sample" element={<SamplePage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Sentry.ErrorBoundary>
      </main>
      <footer className="footer" role="contentinfo">
        <p>
          {/* Footer links use <Link> for client-side routing so the SPA shell + state stays
              mounted across navigation. Plain <a href> here causes a full document reload
              which (a) remounts the ConsentBanner whose initial state reads document.cookie,
              and (b) means the user briefly sees the prerendered HTML before React hydrates
              and re-hides the banner. The mailto: link stays as <a> intentionally. */}
          EAA Proof &middot; <Link to="/pricing">Pricing</Link> &middot;{' '}
          <Link to="/sample">Sample report</Link> &middot;{' '}
          <Link to="/faq">FAQ</Link> &middot;{' '}
          <Link to="/about">About</Link> &middot;{' '}
          <Link to="/changelog">Changelog</Link> &middot;{' '}
          <Link to="/status">Status</Link> &middot;{' '}
          <Link to="/roi-calculator">EAA penalties</Link> &middot;{' '}
          <Link to="/accessibility">Accessibility</Link> &middot;{' '}
          <Link to="/privacy">Privacy</Link> &middot;{' '}
          <Link to="/terms">Terms</Link> &middot;{' '}
          <Link to="/refund-policy">Refunds</Link> &middot;{' '}
          <Link to="/dpa">DPA</Link> &middot;{' '}
          {/* <Link to="/imprint">Imprint</Link> &middot;{' '} — re-enable once legal data filled in */}
          <button
            type="button"
            className="link-button"
            onClick={() => window.dispatchEvent(new Event('eaa-open-consent'))}
          >
            Cookie settings
          </button>{' '}
          &middot; <a href="mailto:support@eaaproof.com">Contact</a>
        </p>
        <p className="footer-disclaimer">
          EAA Proof is an independent third-party accessibility scanning service. We are not
          affiliated with, endorsed by, or representing the European Union, the European
          Commission, any EU regulatory body, or any authority responsible for the European
          Accessibility Act. Our reports identify potential WCAG 2.1 issues and produce a draft
          accessibility statement template; they do not constitute legal compliance certification,
          regulatory clearance, or guarantee against legal liability. Customers remain solely
          responsible for their own compliance review and legal advice.
        </p>
      </footer>
      <StickyCta />
      <ConsentBanner />
    </div>
  );
}

function RouteErrorFallback() {
  return (
    <section style={{ padding: '4rem 1.5rem', textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
      <p style={{ color: 'var(--color-text-muted, #555)', marginBottom: '1.5rem' }}>
        The error was reported automatically. Reloading the page usually fixes it; if not,
        email <a href="mailto:support@eaaproof.com">support@eaaproof.com</a>.
      </p>
      <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
        Reload page
      </button>
    </section>
  );
}
